import {
  CreateParentContent,
  CreateParentForm,
  UpdateExistingParentContent,
  useSchoolEmailInUseValidation,
} from '@schooly/components/applications';
import { useAuth } from '@schooly/components/authentication';
import { useCustomFields } from '@schooly/hooks/use-custom-fields';
import { Loading, ModalSmall } from '@schooly/style';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useApplication } from '../../../context/applications/useApplication';
import { useRouter } from '../../../context/router/useRouter';
import { useApplicationForm } from '../useApplicationForm';

export const UpdateParentModal = () => {
  const navigate = useNavigate();
  const { goBack } = useRouter();
  const { schoolId = '' } = useAuth();
  const emailValidation = useSchoolEmailInUseValidation();

  const { applicationId, fetching, setPendingParents, addParentModal } = useApplication();
  const { parents, addParents, removeParent } = useApplicationForm('update');
  const { applicationAdultCustomFields, fetching: loadingCustomFields } = useCustomFields({
    refetchOnMount: 'always',
  });

  const { parentId } = useParams<'parentId'>();

  const currentParent = useMemo(() => {
    if (parentId) {
      return parents?.find((p) => p.id === parentId);
    }
  }, [parentId, parents]);

  const customFields = currentParent?.custom_fields || applicationAdultCustomFields;
  const hasCustomFields = !!customFields.length;
  const isExistingParent = !!currentParent?.profile_id;
  const isLoading = fetching || loadingCustomFields;

  const onSubmit = useCallback(
    (data: CreateParentForm) => {
      addParents([data]);
      navigate(`/applications/${applicationId}/update`);
    },
    [addParents, applicationId, navigate],
  );

  const onRemoveParent = useCallback(
    (parentId: string) => {
      removeParent(parentId);
      navigate(`/applications/${applicationId}/update`);
    },
    [applicationId, navigate, removeParent],
  );

  const onAddExistingAdult = useCallback(
    (adult: CreateParentForm) => {
      const isParentAdded = parents.some((p) => p.profile_id === adult.profile_id);
      if (isParentAdded) {
        navigate(`/applications/${applicationId}/update`);
        return;
      }

      const hasAdultCustomFields =
        adult.custom_fields.length > 0 || applicationAdultCustomFields.length > 0;

      if (hasAdultCustomFields) {
        setPendingParents([...parents, adult]);
        addParentModal.onOpen();
      } else {
        addParents([adult]);
      }

      navigate(`/applications/${applicationId}/update`);
    },
    [
      addParents,
      applicationAdultCustomFields.length,
      applicationId,
      navigate,
      addParentModal,
      parents,
      setPendingParents,
    ],
  );

  const renderContent = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (isExistingParent && hasCustomFields) {
      return (
        <UpdateExistingParentContent onClose={goBack} onSubmit={onSubmit} parent={currentParent} />
      );
    }

    return (
      <CreateParentContent
        onClose={goBack}
        onSubmit={onSubmit}
        onRemoveParent={onRemoveParent}
        parent={currentParent}
        formCustomFields={customFields}
        schoolId={schoolId}
        onAddExistingAdult={onAddExistingAdult}
        parents={parents}
        emailValidation={emailValidation}
      />
    );
  };

  return <ModalSmall open>{renderContent()}</ModalSmall>;
};
